import { useState, useRef, FC, useEffect } from "react";
import styled, { css } from "styled-components";
import { CreateFormTheme } from "../../utils/themes";
import ChevronDownIcon from "../../assets/icons/ChevronDownIcon";
import StyledText from "../StyledComponents/StyledText";
import { useTheme } from "../../middleware/ThemeContext";

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SelectButton = styled("button")<{
  $isOpen?: boolean;
  $hasError?: boolean;
  $disabled?: boolean;
}>(({ theme, $isOpen, $hasError, $disabled }) => {
  return {
    width: "100%",
    padding: "12px 15px",
    gap: "8px",
    border: `1px solid ${$hasError
      ? theme.CreateFormTheme.colors.error
      : theme.CreateFormTheme.colors.inputBorders
      }`,
    borderRadius: CreateFormTheme.borderRadius,
    background: theme.CreateFormTheme.colors.inputBackground,
    color: theme.CreateFormTheme.colors.inputColor,
    fontWeight: 500,
    fontSize: "14px",
    fontFamily: '"Raleway", sans-serif',
    textAlign: "left",
    cursor: $disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "all 0.3s",
    maxHeight: "42px",
    "&:hover": {
      background: theme.CreateFormTheme.colors.inputBackground,
    },
    opacity: $disabled ? 0.7 : 1,
    pointerEvents: $disabled ? 'none' : 'auto',
  };
});

export const OptionsList = styled.div<{ direction?: "up" | "down" }>`
  position: absolute;
  left: 0;
  width: 100%;
  background: ${({ theme }) => theme.CreateFormTheme.colors.inputBackground};
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
  border-radius: 0 0 ${CreateFormTheme.borderRadius} ${CreateFormTheme.borderRadius};
  z-index: 100;
  overflow-y: auto;
  max-height: 200px;
  // box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
box-shadow: -4px 4px 40px 0px #0000001A;

  border-image: ${({ theme }) => theme.CorporateTheme.linearGradient};
  border-image-slice: 1;

  ${({ direction }) =>
    direction === "up"
      ? css`
          bottom: 100%;
          margin-bottom: 4px;
        `
      : css`
          top: 100%;
          margin-top: 4px;
        `}
`;

export const OptionItem = styled.button`
  width: 100%;
  padding: 12px 18px;
  background: none;
  border: none;
  text-align: left;
  font-size: 14px;
  font-family: "Raleway", sans-serif;
  font-weight: 500;
  color: ${({ theme }) => theme.CreateFormTheme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;

  &:hover {
    background: ${({ theme }) => theme.CorporateTheme.colors.filterBG};
    color: ${({ theme }) => theme.CorporateTheme.colors.accent};
  }
`;

export const ChevronWrapper = styled.div<{ $isOpen?: boolean }>`
  transition: transform 0.3s;
  display: flex;
  align-items: center;
  ${({ $isOpen }) => $isOpen && "transform: rotate(180deg);"}
`;

interface CustomSelectProps {
  options: string[] | { label?: string; value?: number | string }[];
  value: string | { label?: string; value?: string };
  onChange: (
    value: string | { label?: string; value?: string | number }
  ) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  emptyMessage?: string;
  style?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  searchable?: boolean; // ✅ new
  disabled?: boolean;
}

const CustomSelect: FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  style,
  error,
  buttonStyle,
  emptyMessage = "No options found",
  searchable = false,
  disabled = false,
}) => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [direction, setDirection] = useState<"up" | "down">("down");
  const [search, setSearch] = useState("");
  const [selectedValue, setSelectedValue] = useState<
    string | { label: string; value: string } | null
  >(
    typeof value === "object" && value !== null
      ? { label: value.label ?? "", value: String(value.value ?? "") }
      : typeof value === "string"
        ? value
        : null
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (
    selectedValue: string | { label: string; value: string | number }
  ) => {
    const valueToPass =
      typeof selectedValue === "object" && selectedValue !== null
        ? selectedValue
        : selectedValue;

    onChange(valueToPass);
    if (typeof valueToPass === "object" && valueToPass !== null) {
      setSelectedValue({
        label: valueToPass.label,
        value: String(valueToPass.value),
      });
    } else {
      setSelectedValue(valueToPass);
    }
    setIsOpen(false);
    setSearch(""); // reset search after selection
  };


  const normalizedOptions = (options ?? []).map((option) => {
    if (typeof option === "string") {
      return { label: option, value: option };
    }
    return {
      label: option.label ?? String(option.value ?? ""),
      value: option.value ?? "",
    };
  });

  const filteredOptions = normalizedOptions.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    // Keep selectedValue in sync when the parent updates `value`
    const next =
      typeof value === "object" && value !== null
        ? { label: value.label ?? "", value: String(value.value ?? "") }
        : typeof value === "string"
          ? value
          : null;
    setSelectedValue(next);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const dropdownHeight = 200;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDirection("up");
      } else {
        setDirection("down");
      }

      if (searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }, [isOpen, searchable]);

  return (
    <SelectContainer style={style} ref={containerRef}>
      <SelectButton
        theme={theme}
        style={buttonStyle}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        $isOpen={isOpen}
        $hasError={!!error}
        $disabled={disabled}
        disabled={disabled}
      >
        {typeof selectedValue === "object" && selectedValue !== null
          ? selectedValue.label
          : selectedValue || placeholder || "Select an option"}
        <ChevronWrapper $isOpen={isOpen}>
          <ChevronDownIcon color={theme.CorporateTheme.colors.textSecondary} />
        </ChevronWrapper>
      </SelectButton>

      {isOpen && (
        <OptionsList theme={theme} direction={direction}>
          {searchable && (
            <div style={{ padding: "8px" }}>
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                style={{
                  width: "100%",
                  padding: "6px 10px",
                  border: `1px solid ${theme.CreateFormTheme.colors.inputBorders}`,
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
            </div>
          )}

          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <OptionItem
                theme={theme}
                key={opt.value}
                onClick={() => handleSelect(opt)}
              >
                {opt.label}
              </OptionItem>
            ))
          ) : (
            <div className="d-flex align-items-center justify-content-center p-3">
              <StyledText>{emptyMessage}</StyledText>
            </div>
          )}

        </OptionsList>
      )}
    </SelectContainer>
  );
};

export default CustomSelect;
